<!-- App.vue -->
<template>
  <div id="app" :class="['app-wrapper', `theme-${currentTheme}`]">
    
    <NavigationBar
      :current-user="currentUser" 
      :user-claims="userClaims" 
      :current-theme="currentTheme" 
      @toggle-theme="toggleTheme">
      <router-view />
    </NavigationBar>
    
    <!-- Footer - only show on homepage -->
    <AppFooter v-if="isHomePage" />
  </div>
</template>

<script lang="js">
import { auth } from '@/firebase';
import NavigationBar from '@/nav/SidebarNav.vue';
import AppFooter from '@/nav/FooterView.vue';

export default {
  name: 'App',
  components: {
    NavigationBar,
    AppFooter,
    // ThemeProvider
  },
  data() {
    return {
      currentTheme: localStorage.getItem('app-theme') || 'light',
      themeOptions: ['light', 'dark'],
      currentUser: null,
      userClaims: null
    };
  },
  computed: {
    // Only show footer on homepage
    isHomePage() {
      return this.$route.path === '/';
    },
    
    themeIcon() {
      switch (this.currentTheme) {
        case 'light': return 'ic ic-moon';
        case 'dark': return 'ic ic-sun';
        case 'glass': return 'ic ic-light';
        default: return 'ic ic-sun';
      }
    },
    
    nextTheme() {
      const currentIndex = this.themeOptions.indexOf(this.currentTheme);
      return this.themeOptions[(currentIndex + 1) % this.themeOptions.length];
    }
  },
  methods: {
    toggleTheme() {
      const currentIndex = this.themeOptions.indexOf(this.currentTheme);
      this.currentTheme = this.themeOptions[(currentIndex + 1) % this.themeOptions.length];
      localStorage.setItem('app-theme', this.currentTheme);
      document.documentElement.setAttribute('data-theme', this.currentTheme);
    },
    
    async getClaims() {
      if (!auth.currentUser) return null;
      
      try {
        const idTokenResult = await auth.currentUser.getIdTokenResult(true);
        this.userClaims = idTokenResult.claims;
        return idTokenResult.claims;
      } catch (error) {
        console.error('Error fetching user claims:', error);
        return null;
      }
    },
    
    updateHeight() {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight}px`);
    }
  },
  mounted() {
    // get domain and path
    const domain = window.location.hostname;
    const path = window.location.pathname.replace(/\/$/, '');

    // if path is empty and domain=selfgens.io, redirect to home.selfgens.io
    if (path === '' ) {
      if (domain === 'selfgens.io'){
        let url = `https://home.${window.location.hostname}`;
        window.location.href = url;
      }
    }
    
    // Apply initial theme
    document.documentElement.setAttribute('data-theme', this.currentTheme);
    
    // Handle window resizing for mobile viewport calculations
    window.addEventListener('resize', this.updateHeight);
    this.updateHeight();
    
    // Auth state listener
    this.unsubscribe = auth.onAuthStateChanged(async (user) => {
      this.currentUser = user;
      
      if (user) {
        this.userClaims = await this.getClaims();
      } else {
        this.userClaims = null;
      }
    });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateHeight);
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  },
  provide() {
    return {
      currentUser: () => this.currentUser,
      userClaims: () => this.userClaims,
      theme: () => this.currentTheme
    };
  }
}
</script>

<style scoped lang="scss">
.app-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.theme-toggle { 
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  z-index: 1000;
}

.theme-button {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background-color: var(--primary-color);
  color: white;
  border: none;
  box-shadow: var(--shadow-md);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.theme-button:hover {
  background-color: var(--primary-dark);
  transform: translateY(-2px);
}

.theme-button i {
  font-size: 1.5rem;
}

.theme-glass .theme-button {
  backdrop-filter: blur(4px);
  background-color: rgba(10, 151, 217, 0.7);
}
</style>